import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

export interface BodyGesture {
  leftArmUp: boolean;
  rightArmUp: boolean;
  bothArmsUp: boolean;
  armsCrossed: boolean;
  waving: boolean;
  punching: boolean;
}

export class BodyTracker {
  private poseLandmarker: PoseLandmarker | null = null;
  private video: HTMLVideoElement | null = null;
  private running = false;
  private gesture: BodyGesture = {
    leftArmUp: false,
    rightArmUp: false,
    bothArmsUp: false,
    armsCrossed: false,
    waving: false,
    punching: false,
  };
  private lastWaveTime = 0;
  private lastPunchTime = 0;
  private waveCooldown = 800;
  private punchCooldown = 500;
  private lastLandmarks: any[] | null = null;

  async init(videoElement?: HTMLVideoElement): Promise<boolean> {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm'
      );
      this.poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task',
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numPoses: 1,
      });

      if (videoElement) {
        this.video = videoElement;
      } else {
        this.video = document.createElement('video');
        this.video.style.display = 'none';
        document.body.appendChild(this.video);

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480, facingMode: 'user' },
        });
        this.video.srcObject = stream;
        await this.video.play();
      }

      this.running = true;
      this.detectLoop();
      return true;
    } catch (e) {
      console.warn('Body tracking init failed:', e);
      return false;
    }
  }

  private detectLoop = () => {
    if (!this.running || !this.video || !this.poseLandmarker || !this.video.videoWidth) {
      if (this.running) requestAnimationFrame(this.detectLoop);
      return;
    }

    const results = this.poseLandmarker.detectForVideo(this.video, performance.now());
    if (results.landmarks && results.landmarks.length > 0) {
      const lm = results.landmarks[0];
      this.lastLandmarks = lm;
      this.gesture = this.computeGesture(lm);
    } else {
      this.lastLandmarks = null;
      this.gesture = {
        leftArmUp: false, rightArmUp: false, bothArmsUp: false,
        armsCrossed: false, waving: false, punching: false,
      };
    }

    requestAnimationFrame(this.detectLoop);
  };

  private computeGesture(landmarks: any[]): BodyGesture {
    const nose = landmarks[0];
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftElbow = landmarks[13];
    const rightElbow = landmarks[14];
    const leftWrist = landmarks[15];
    const rightWrist = landmarks[16];
    const leftHip = landmarks[23];
    const rightHip = landmarks[24];

    const gesture: BodyGesture = {
      leftArmUp: false,
      rightArmUp: false,
      bothArmsUp: false,
      armsCrossed: false,
      waving: false,
      punching: false,
    };

    // Left arm up: wrist above shoulder
    if (leftWrist.y < leftShoulder.y - 0.05) {
      gesture.leftArmUp = true;
    }

    // Right arm up: wrist above shoulder
    if (rightWrist.y < rightShoulder.y - 0.05) {
      gesture.rightArmUp = true;
    }

    gesture.bothArmsUp = gesture.leftArmUp && gesture.rightArmUp;

    // Arms crossed: each wrist past the opposite shoulder
    if (leftWrist.x > rightShoulder.x && rightWrist.x < leftShoulder.x) {
      gesture.armsCrossed = true;
    }

    // Waving: wrist moving side to side rapidly (check wrist x relative to shoulder)
    const now = performance.now();
    if (rightWrist.y < rightShoulder.y && Math.abs(rightWrist.x - rightShoulder.x) > 0.15) {
      if (now - this.lastWaveTime > this.waveCooldown) {
        gesture.waving = true;
        this.lastWaveTime = now;
      }
    }

    // Punching: one arm extended forward (wrist far from body, elbow extended)
    const leftArmExtended = leftElbow.y < leftShoulder.y && leftWrist.y < leftElbow.y;
    const rightArmExtended = rightElbow.y < rightShoulder.y && rightWrist.y < rightElbow.y;
    if ((leftArmExtended || rightArmExtended) && now - this.lastPunchTime > this.punchCooldown) {
      gesture.punching = true;
      this.lastPunchTime = now;
    }

    return gesture;
  }

  getGesture(): BodyGesture {
    return this.gesture;
  }

  getLandmarks(): any[] | null {
    return this.lastLandmarks;
  }

  getVideoElement(): HTMLVideoElement | null {
    return this.video;
  }

  stop() {
    this.running = false;
    if (this.video) {
      const stream = this.video.srcObject as MediaStream;
      if (stream) stream.getTracks().forEach(t => t.stop());
      this.video.remove();
      this.video = null;
    }
  }
}
